'use client';

import {useAuth} from 'iq-auth';
import {GuideFeedbackDialog} from 'iq-interview_guides/components/feedback/guide_feedback_dialog';
import {getGuideVote, postGuideVote} from 'iq-interview_guides/feedback_api';
import {trackGuideFeedbackVote} from 'iq-interview_guides/tracking';
import {ThumbsDown, ThumbsUp} from 'lucide-react';
import {useEffect, useState} from 'react';

type Phase = 'loading' | 'ask' | 'thanks' | 'hidden';

// "Is this guide helpful?" for logged-in readers. Renders nothing for logged-out users and
// for anyone who already voted on this guide.
export function GuideFeedback({slug}: {slug: string}) {
  const {user} = useAuth();
  if (!user.loggedIn) return null;
  return <GuideFeedbackPrompt slug={slug} />;
}

function GuideFeedbackPrompt({slug}: {slug: string}) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getGuideVote(slug)
      .then((vote) => {
        if (cancelled) return;
        setPhase(vote === 0 ? 'ask' : 'hidden');
      })
      .catch(() => {
        if (!cancelled) setPhase('hidden');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const vote = (value: 1 | -1) => {
    void trackGuideFeedbackVote({slug, vote: value});
    void postGuideVote(slug, value).catch(() => {});
    setPhase('thanks');
    if (value === -1) setDialogOpen(true);
  };

  if (phase === 'loading' || phase === 'hidden') return null;

  return (
    <section className='mt-12 rounded-xl border border-iq-grey-light bg-iq-grey-lighter px-5 py-4'>
      <FeedbackBody phase={phase} onVote={vote} />
      <GuideFeedbackDialog slug={slug} open={dialogOpen} onOpenChange={setDialogOpen} />
    </section>
  );
}

function FeedbackBody({phase, onVote}: {phase: Phase; onVote: (value: 1 | -1) => void}) {
  if (phase === 'thanks') {
    return <p className='text-sm font-semibold text-iq-black'>Thanks for the feedback.</p>;
  }
  return (
    <div className='flex flex-wrap items-center gap-x-4 gap-y-3'>
      <p className='text-sm font-semibold text-iq-black'>Is this guide helpful?</p>
      <div className='flex gap-2'>
        <VoteButton label='Yes' onClick={() => onVote(1)} icon={<ThumbsUp size={14} />} />
        <VoteButton label='No' onClick={() => onVote(-1)} icon={<ThumbsDown size={14} />} />
      </div>
    </div>
  );
}

function VoteButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className='inline-flex items-center gap-2 rounded-lg border border-iq-grey-light bg-white px-4 py-2 text-sm font-semibold text-iq-grey-dark transition-colors hover:border-iq-orange hover:text-iq-orange-dark'>
      {icon}
      {label}
    </button>
  );
}
